import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import { Label } from '../ui/label'
import { mdiVideoOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { AnnotationCanvas } from './AnnotationCanvas'
import { AnnotationToolbar } from './AnnotationToolbar'
import { useAuth } from '../../contexts/AuthContext'
import { useIsMobile } from '../../hooks/use-mobile'
import { apiTraceSummary, getApiTrace } from '../../lib/apiTrace'
import type { Annotation, AnnotationTool } from './types'

// MAX_COMMENT_CHARS must stay identical to the backend's
// maxIssueCommentChars (internal/handlers/feedback/diagnostics.go) — the
// server caps the comment before rendering the GitHub issue body (spec
// 2026-09-08-01) regardless of what the client sends, so a drifted client
// cap would just mean silent server-side truncation with no warning here.
const MAX_COMMENT_CHARS = 8192

interface FeedbackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  screenshot: Blob | null
  video: Blob | null
  isCapturing: boolean
  onStartRecording: () => void
  onSubmit: (comment: string, annotations: Annotation[]) => Promise<void>
}

function parseBrowser(ua: string): string {
  if (ua.includes('Firefox/')) {
    const match = ua.match(/Firefox\/([\d.]+)/)
    return `Firefox ${match?.[1] ?? ''}`
  }
  if (ua.includes('Edg/')) {
    const match = ua.match(/Edg\/([\d.]+)/)
    return `Edge ${match?.[1] ?? ''}`
  }
  if (ua.includes('Chrome/')) {
    const match = ua.match(/Chrome\/([\d.]+)/)
    return `Chrome ${match?.[1]?.split('.')[0] ?? ''}`
  }
  if (ua.includes('Safari/') && ua.includes('Version/')) {
    const match = ua.match(/Version\/([\d.]+)/)
    return `Safari ${match?.[1]?.split('.')[0] ?? ''}`
  }
  return 'Browser'
}

export function FeedbackDialog({
  open,
  onOpenChange,
  screenshot,
  video,
  isCapturing,
  onStartRecording,
  onSubmit,
}: FeedbackDialogProps) {
  const { t } = useTranslation('feedback')
  const { user } = useAuth()
  const commentRef = useRef<HTMLTextAreaElement>(null)
  // useIsMobile() (not a one-shot window.innerWidth read) so rotating the
  // device or resizing the window keeps the layout in sync.
  const isMobile = useIsMobile()

  const [annotations, setAnnotations] = useState<Annotation[]>([])
  const [comment, setComment] = useState('')
  const [tool, setTool] = useState<AnnotationTool>('rect')
  const [color, setColor] = useState('#ef4444')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Build screenshot URL for preview
  const screenshotUrl = useMemo(() => {
    if (!screenshot) return null
    return URL.createObjectURL(screenshot)
  }, [screenshot])

  // Build video URL for preview
  const videoUrl = useMemo(() => {
    if (!video) return null
    return URL.createObjectURL(video)
  }, [video])

  // Revoke object URLs when dialog closes or media changes
  useEffect(() => {
    return () => {
      if (screenshotUrl) URL.revokeObjectURL(screenshotUrl)
    }
  }, [screenshotUrl])

  useEffect(() => {
    return () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl)
    }
  }, [videoUrl])

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setAnnotations([])
      setComment('')
      setSubmitError(null)
      setSuccessMessage(null)
      setTool('rect')
      setColor('#ef4444')
    }
  }, [open])

  // Auto-focus comment textarea on mobile
  useEffect(() => {
    if (open && isMobile) {
      setTimeout(() => commentRef.current?.focus(), 200)
    }
  }, [open, isMobile])

  const handleAddAnnotation = useCallback((ann: Annotation) => {
    setAnnotations((prev) => [...prev, ann])
  }, [])

  const handleUndo = useCallback(() => {
    setAnnotations((prev) => prev.slice(0, -1))
  }, [])

  const handleSubmit = async () => {
    setSubmitError(null)
    setIsSubmitting(true)
    try {
      await onSubmit(comment, annotations)
      setSuccessMessage(t('success'))
      // Close after brief success message
      setTimeout(() => {
        onOpenChange(false)
      }, 800)
    } catch {
      setSubmitError(t('error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  // Context line. Recomputed on `open` (not just mount) so the request count
  // reflects the trace at the moment the reporter opens the dialog, not
  // whatever it was when the dialog first mounted.
  const contextLine = useMemo(() => {
    const path = window.location.pathname
    const browser = parseBrowser(navigator.userAgent)
    const viewport = `${window.innerWidth}×${window.innerHeight}`
    const parts = [path, browser, viewport]
    if (user?.name) parts.push(user.name)
    let line = parts.join(' • ')

    const summary = apiTraceSummary(getApiTrace())
    if (summary) {
      line += ' · ' + t('context_requests', { count: summary.count, failed: summary.failed })
    }
    return line
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, open, t])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="feedback-dialog"
        // max-h-[90dvh] + vertical scroll keeps the footer reachable when the
        // mobile on-screen keyboard shrinks the viewport; `dvh` (not `vh`) so
        // the collapsing URL bar is accounted for. Horizontal overflow stays
        // hidden as a safety net — the real fix is the shrinkable grid column
        // in ui/dialog.tsx (#1181).
        className="w-[calc(100vw-1rem)] max-w-sm md:max-w-4xl md:w-full p-0 gap-0 max-h-[90dvh] overflow-y-auto overflow-x-hidden"
      >
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle>{t('dialog_title')}</DialogTitle>
        </DialogHeader>

        <div className="hidden md:flex md:flex-row gap-0 overflow-hidden">
          {/* Left: Screenshot/Video preview */}
          <div className="flex-1 min-h-48 max-h-96 md:max-h-[60vh] overflow-auto bg-muted/20 border-b md:border-b-0 md:border-r relative">
            {videoUrl ? (
              <video
                src={videoUrl}
                controls
                className="w-full block"
                data-testid="feedback-video-preview"
              />
            ) : screenshotUrl ? (
              <div className="relative inline-block w-full">
                <img
                  src={screenshotUrl}
                  alt="Screenshot"
                  className="w-full block"
                  draggable={false}
                  data-testid="feedback-screenshot-preview"
                />
                <AnnotationCanvas
                  annotations={annotations}
                  tool={tool}
                  color={color}
                  onAddAnnotation={handleAddAnnotation}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full min-h-48 text-muted-foreground text-sm gap-2">
                {isCapturing ? (
                  <>
                    <span className="motion-safe:animate-spin">⏳</span>
                    {t('capturing')}
                  </>
                ) : (
                  t('no_screenshot')
                )}
              </div>
            )}
          </div>

          {/* Right: Toolbar */}
          <div className="md:w-40 p-3 flex-shrink-0">
            {!videoUrl && (
              <AnnotationToolbar
                tool={tool}
                color={color}
                onToolChange={setTool}
                onColorChange={setColor}
                onUndo={handleUndo}
                canUndo={annotations.length > 0}
              />
            )}
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2 mt-3 whitespace-nowrap"
              onClick={onStartRecording}
              title={t(videoUrl ? 'rerecord_hint' : 'record_hint')}
              aria-label={t(videoUrl ? 'rerecord_hint' : 'record_hint')}
              data-testid="feedback-record-btn"
            >
              <AppIcon path={mdiVideoOutline} size="sm" />
              <span className="text-xs">{t(videoUrl ? 'rerecord' : 'record')}</span>
            </Button>
          </div>
        </div>

        {/* Comment section */}
        <div className="px-4 py-3 space-y-2 border-t">
          <Label htmlFor="feedback-comment-input">{t('comment_label')}</Label>
          <Textarea
            ref={commentRef}
            id="feedback-comment-input"
            data-testid="feedback-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('comment_placeholder')}
            className="resize-none"
            rows={isMobile ? 5 : 3}
            maxLength={MAX_COMMENT_CHARS}
          />
          {comment.length >= MAX_COMMENT_CHARS && (
            <p className="text-xs text-muted-foreground" data-testid="feedback-comment-limit-hint">
              {t('comment_limit_reached', { max: MAX_COMMENT_CHARS })}
            </p>
          )}
        </div>

        {/* Context line — min-w-0 so `truncate` can actually clip the long
            path/browser/viewport string instead of widening the dialog. */}
        <div className="px-4 py-1.5 border-t min-w-0">
          <p className="text-xs text-muted-foreground truncate" data-testid="feedback-context-line">
            {contextLine}
          </p>
        </div>

        {/* Error / success message */}
        {(submitError || successMessage) && (
          <div className="px-4 py-1.5">
            {submitError && (
              <p className="text-sm text-destructive">{submitError}</p>
            )}
            {successMessage && (
              <p className="text-sm text-green-600 dark:text-green-400">{successMessage}</p>
            )}
          </div>
        )}

        {/* sticky: the dialog body scrolls, the actions never do. */}
        <DialogFooter className="sticky bottom-0 z-10 bg-background px-4 py-3 border-t flex-row justify-end gap-2 space-x-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            data-testid="feedback-cancel-btn"
          >
            {t('cancel')}
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isSubmitting}
            data-testid="feedback-submit-btn"
          >
            {isSubmitting ? '...' : t('submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
